import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import type { z } from "zod";
import { useNavigate, useParams } from "react-router-dom";
import { PricingInput } from "@reina/shared";
import {
  Button,
  EmptyState,
  Field,
  FieldGrid,
  Modal,
  Panel,
  PanelBody,
  PanelHead,
  Pill,
  TableWrap,
  useToast,
} from "../../components";
import { useCreatePricing, useProject, useProjectPricings } from "../../api/pricings";
import { errorMessage, formatDate } from "../../lib/format";
import { textOrNull } from "../../lib/form";
import { pricingStatusLabel, pricingStatusTone } from "../../lib/tones";
import { DeletePricingModal } from "./PricingDetailPage";
import { ProjectModal } from "./ProjectModal";

export function ProjectDetailPage() {
  const { id = "" } = useParams();
  const navigate = useNavigate();
  const project = useProject(id);
  const pricings = useProjectPricings(id);
  const [deleteTarget, setDeleteTarget] = useState<{
    id: string;
    projectId: string;
    name: string;
  } | null>(null);
  const [editOpen, setEditOpen] = useState(false);
  const [newPricingOpen, setNewPricingOpen] = useState(false);

  if (project.isPending) {
    return (
      <Panel>
        <PanelBody>
          <EmptyState title="Loading project…" />
        </PanelBody>
      </Panel>
    );
  }
  if (project.isError || !project.data) {
    return (
      <Panel>
        <PanelBody>
          <EmptyState
            title="Could not load project"
            hint={project.error ? errorMessage(project.error) : undefined}
            action={<Button onClick={() => navigate("/projects")}>Back to projects</Button>}
          />
        </PanelBody>
      </Panel>
    );
  }

  const data = project.data;
  const items = pricings.data?.items ?? [];

  const newPricingButton = (
    <Button variant="primary" onClick={() => setNewPricingOpen(true)}>
      New pricing
    </Button>
  );

  return (
    <div className="stack">
      <div className="breadcrumb">
        <button type="button" onClick={() => navigate("/projects")}>
          Projects
        </button>
        <span>/</span>
        <span>{data.name}</span>
      </div>

      <Panel>
        <PanelHead
          title={data.name}
          sub={data.clientDeveloper ?? "no client / developer set"}
          actions={
            <>
              <Pill tone={data.status === "active" ? "ok" : "neutral"}>
                {data.status === "active" ? "Active" : "Archived"}
              </Pill>
              <Button onClick={() => setEditOpen(true)}>Edit</Button>
            </>
          }
        />
        {data.description && (
          <PanelBody>
            <p style={{ margin: 0 }}>{data.description}</p>
          </PanelBody>
        )}
      </Panel>

      <Panel>
        <PanelHead
          title="Pricings"
          sub={
            pricings.data
              ? `${pricings.data.total} run${pricings.data.total === 1 ? "" : "s"} — named + dated, history kept`
              : "named, dated pricing runs"
          }
          actions={newPricingButton}
        />
        {pricings.isPending ? (
          <PanelBody>
            <EmptyState title="Loading pricings…" />
          </PanelBody>
        ) : pricings.isError ? (
          <PanelBody>
            <EmptyState title="Could not load pricings" hint={errorMessage(pricings.error)} />
          </PanelBody>
        ) : items.length === 0 ? (
          <PanelBody>
            <EmptyState
              title="No pricings yet"
              hint="Add a named pricing run, then paste the buy-list to compare suppliers."
              action={newPricingButton}
            />
          </PanelBody>
        ) : (
          <TableWrap>
            <thead>
              <tr>
                <th>Pricing</th>
                <th>Run date</th>
                <th>As-at date</th>
                <th>Status</th>
                <th aria-label="Actions" />
              </tr>
            </thead>
            <tbody>
              {items.map((pricing) => (
                <tr
                  key={pricing.id}
                  onClick={() => navigate(`/pricings/${pricing.id}`)}
                  style={{ cursor: "pointer" }}
                >
                  <td>
                    <strong>{pricing.name}</strong>
                  </td>
                  <td className="mono">{formatDate(pricing.runDate)}</td>
                  <td className="mono">{formatDate(pricing.asAtDate)}</td>
                  <td>
                    <Pill tone={pricingStatusTone[pricing.status]}>
                      {pricingStatusLabel[pricing.status]}
                    </Pill>
                  </td>
                  <td>
                    <div className="actions">
                      <Button
                        mini
                        onClick={(event) => {
                          event.stopPropagation();
                          navigate(`/pricings/${pricing.id}`);
                        }}
                      >
                        Open
                      </Button>
                      <Button
                        mini
                        variant="danger"
                        onClick={(event) => {
                          event.stopPropagation();
                          setDeleteTarget({
                            id: pricing.id,
                            projectId: pricing.projectId,
                            name: pricing.name,
                          });
                        }}
                      >
                        Delete
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </TableWrap>
        )}
      </Panel>

      {editOpen && <ProjectModal project={data} onClose={() => setEditOpen(false)} />}
      {newPricingOpen && (
        <NewPricingModal projectId={data.id} onClose={() => setNewPricingOpen(false)} />
      )}
      {deleteTarget && (
        <DeletePricingModal pricing={deleteTarget} onClose={() => setDeleteTarget(null)} />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// New pricing modal (runDate defaults to today; asAtDate optional)
// ---------------------------------------------------------------------------

function NewPricingModal({ projectId, onClose }: { projectId: string; onClose: () => void }) {
  const toast = useToast();
  const navigate = useNavigate();
  const create = useCreatePricing();

  const today = new Date().toISOString().slice(0, 10);
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<z.input<typeof PricingInput>, unknown, z.output<typeof PricingInput>>({
    resolver: zodResolver(PricingInput),
    defaultValues: { name: "", runDate: today, asAtDate: "" },
  });

  const onSubmit = handleSubmit(async (values) => {
    try {
      const created = await create.mutateAsync({ projectId, input: values });
      toast.push({ message: "Pricing created", tone: "ok" });
      onClose();
      navigate(`/pricings/${created.id}`);
    } catch (error) {
      toast.push({ message: errorMessage(error), tone: "danger" });
    }
  });

  return (
    <Modal
      open
      title="New pricing"
      sub="a named, dated run — e.g. “Kingsnorth Ph2 — May tender”"
      onClose={onClose}
      footer={
        <>
          <span className="sub">Set an as-at date to price against historic prices.</span>
          <div className="actions">
            <Button variant="ghost" onClick={onClose}>
              Cancel
            </Button>
            <Button variant="primary" type="submit" form="pricing-form" disabled={isSubmitting}>
              Create pricing
            </Button>
          </div>
        </>
      }
    >
      <form id="pricing-form" className="stack" onSubmit={onSubmit} noValidate>
        <Field label="Name" error={errors.name?.message}>
          <input className="field" autoFocus {...register("name")} />
        </Field>
        <FieldGrid>
          <Field label="Run date" error={errors.runDate?.message}>
            <input className="field" type="date" {...register("runDate")} />
          </Field>
          <Field label="As-at date (optional)" error={errors.asAtDate?.message}>
            <input className="field" type="date" {...register("asAtDate", { setValueAs: textOrNull })} />
          </Field>
        </FieldGrid>
      </form>
    </Modal>
  );
}
